import type { ClientMessage, ServerMessage } from './protocol';
import { apiBase } from './apiBase';

export interface NetHandlers {
  onMessage(msg: ServerMessage): void;
  onOpen?(): void;
  onClose?(): void;
  onError?(): void;
}

/** Thin WebSocket wrapper for the room server. */
export class NetClient {
  private ws: WebSocket | null = null;

  constructor(private readonly url: string, private readonly handlers: NetHandlers) {}

  connect(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => this.handlers.onOpen?.();
    ws.onclose = () => this.handlers.onClose?.();
    ws.onerror = () => this.handlers.onError?.();
    ws.onmessage = (e) => {
      try {
        this.handlers.onMessage(JSON.parse(e.data as string) as ServerMessage);
      } catch {
        /* ignore malformed frames */
      }
    };
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Close the socket. `farewell` is a last message to get out first — used for
   * `leaveRoom`, where it matters a great deal that the server sees an explicit
   * leave (which frees the seat immediately) rather than just a dropped socket
   * (which holds the seat open for the reconnect grace window, leaving the room
   * still believing the player is in it).
   */
  close(farewell?: ClientMessage): void {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    // Detach handlers first: closing a socket doesn't cancel a message frame
    // already in flight from the server, and that frame would otherwise still
    // reach the caller's onMessage after they've considered themselves offline.
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    if (farewell && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(farewell));
      // Yield once so the frame is on the wire before the closing handshake.
      setTimeout(() => ws.close(), 0);
      return;
    }
    ws.close();
  }
}

/**
 * Default server URL: the SAME origin as the page, on the `/ws` path (the room
 * server serves the app and the WebSocket on one port). Uses `wss://` when the
 * page is HTTPS. Override entirely with `VITE_WS_URL`.
 */
export function defaultServerUrl(): string {
  const env = (import.meta.env.VITE_WS_URL as string | undefined) ?? '';
  if (env) return env;
  // Derive from apiBase (not raw `location`) so a mobile build that only sets
  // VITE_API_URL — and loads from a Capacitor origin with no real server behind
  // it — still points its WebSocket at the actual deployed host.
  const base = apiBase();
  const url = new URL(base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/ws';
  return url.toString();
}
